import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeroImage from './HeroImage';
import { HeroData } from '../../../models/HeroData';

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ContentContainer = styled.div`
  max-width: 1600px;
  width: 100%;
  max-height: 90vh;
  background-color: #0b0d14;
  background-image: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  border: 2px solid #61dafb;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10001;

  &:hover {
    background-color: #d32f2f;
    transform: scale(1.1);
  }
`;

const Title = styled.h2`
  color: #61dafb;
  font-size: 24px;
  margin-bottom: 30px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const PickModeSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const PickModeSection = styled.div`
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 20px;
  border: 2px solid ${props => props.$isFirst ? 'rgba(29, 233, 182, 0.4)' : 'rgba(255, 107, 107, 0.4)'};
`;

const PickModeHeader = styled.h3`
  color: ${props => props.$isFirst ? '#1de9b6' : '#ff6b6b'};
  font-size: 20px;
  margin-bottom: 20px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

const ScenariosGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ScenarioCard = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(97, 218, 251, 0.2);
`;

const ScenarioHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #61dafb;
  margin-bottom: 15px;
  text-align: center;
`;

const MatchupContent = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const HeroPicksColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 260px;
`;

const TeamsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const TeamColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TeamLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  color: ${props => props.$team === 'Radiant' ? '#1de9b6' : '#ff6b6b'};
  text-align: center;
  text-transform: uppercase;
`;

const SmallHeroBox = styled.div`
  width: 104px;
  height: 56px;
  background-color: #111215;
  border-radius: 8px;
  border: 1px solid ${props => props.$team === 'Radiant' ? 'rgba(29, 233, 182, 0.3)' : 'rgba(255, 107, 107, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  ${props => props.$isEmpty && `
    &::after {
      content: '—';
      font-size: 20px;
      color: rgba(255, 255, 255, 0.2);
    }
  `}
`;

const LanesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const LaneRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
`;

const LaneLabel = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: ${props => props.$lane === 'Mid' ? '#61dafb' : '#1de9b6'};
  min-width: 70px;
  text-align: center;
  text-transform: uppercase;
`;

const TeamSlots = styled.div`
  display: flex;
  gap: 8px;
`;

const HeroSlot = styled.div`
  width: 120px;
  height: 65px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.$team === 'Radiant' ? 'rgba(29, 233, 182, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.$team === 'Radiant' ? '#1de9b6' : '#ff6b6b'};
    background-color: rgba(255, 255, 255, 0.08);
    transform: scale(1.05);
  }

  ${props => props.$isEmpty && `
    &::after {
      content: '+';
      font-size: 36px;
      color: rgba(255, 255, 255, 0.3);
    }
  `}
`;

const VersusLabel = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
`;

const NoMatchupsMessage = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  padding: 20px;
`;

// Hero Selection Modal Styled Components
const HeroSelectOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroSelectContainer = styled.div`
  position: fixed;
  z-index: 10003;
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(14, 16, 22, 0.97);
  border: 1px solid #444;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  animation: popupFadeIn 0.25s cubic-bezier(0.23, 1, 0.32, 1);

  @keyframes popupFadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const HeroSelectHeader = styled.div`
  padding: 8px 16px;
  border-bottom: 1px solid #444;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: rgba(0, 0, 0, 0.3);
`;

const HeroSelectHeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #61dafb;
    background-color: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const HeroSelectTitle = styled.div`
  font-weight: bold;
  color: #eee;
  font-size: 14px;
`;

const CloseButtonSmall = styled.div`
  cursor: pointer;
  font-size: 20px;
  color: #888;
  padding: 0 8px;
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 100px));
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 400px;
  flex: 1;
  justify-content: center;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #555;
    border-radius: 4px;

    &:hover {
      background-color: #666;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
`;

const HeroSelectCard = styled.div`
  cursor: pointer;
  padding: 6px 4px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const HeroName = styled.div`
  font-size: 11px;
  margin-top: 6px;
  text-align: center;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  font-weight: 500;
`;

const AllMatchupsView = ({ isOpen, onClose, scenariosFirstPick, scenariosSecondPick, updateScenario }) => {
  const [showHeroSelect, setShowHeroSelect] = useState(false);
  const [heroSearchQuery, setHeroSearchQuery] = useState('');
  const [selectingFor, setSelectingFor] = useState(null); // { pickMode, scenarioId, lane, team, index }

  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showHeroSelect) {
          setShowHeroSelect(false);
          setSelectingFor(null);
          setHeroSearchQuery('');
        } else if (isOpen) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, showHeroSelect, onClose]);

  if (!isOpen) return null;

  // Separate picks by team
  const getTeamPicks = (scenario) => {
    const radiantPositions = [7, 13, 14, 17, 22]; // 0-indexed: 8,14,15,18,23
    const direPositions = [8, 12, 15, 16, 23]; // 0-indexed: 9,13,16,17,24

    const radiant = radiantPositions.map(pos => (scenario.picks || [])[pos]).filter(pick => pick && pick.hero).slice(0, 5);
    const dire = direPositions.map(pos => (scenario.picks || [])[pos]).filter(pick => pick && pick.hero).slice(0, 5);

    return { radiant, dire };
  };

  // Handler for clicking a lane slot
  const handleLaneSlotClick = (pickMode, scenarioId, lane, team, index) => {
    setSelectingFor({ pickMode, scenarioId, lane, team, index });
    setShowHeroSelect(true);
  };

  // Handler for selecting a hero
  const handleHeroSelect = (hero) => {
    if (selectingFor && updateScenario) {
      const { pickMode, scenarioId, lane, team, index } = selectingFor;

      // Get the current scenario
      const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
      const scenario = scenarios.find(s => s.id === scenarioId);

      if (scenario && scenario.practiceMatchups) {
        // Update the matchups
        const newMatchups = {
          ...scenario.practiceMatchups,
          [lane]: {
            ...scenario.practiceMatchups[lane],
            [team]: scenario.practiceMatchups[lane][team].map((h, i) => i === index ? hero : h)
          }
        };

        // Update the scenario
        updateScenario(pickMode, scenarioId, { practiceMatchups: newMatchups });
      }

      setShowHeroSelect(false);
      setSelectingFor(null);
      setHeroSearchQuery('');
    }
  };

  // Handler for clearing a lane slot (right-click)
  const handleClearSlot = (pickMode, scenarioId, lane, team, index) => {
    if (updateScenario) {
      // Get the current scenario
      const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
      const scenario = scenarios.find(s => s.id === scenarioId);

      if (scenario && scenario.practiceMatchups) {
        // Update the matchups
        const newMatchups = {
          ...scenario.practiceMatchups,
          [lane]: {
            ...scenario.practiceMatchups[lane],
            [team]: scenario.practiceMatchups[lane][team].map((h, i) => i === index ? null : h)
          }
        };

        // Update the scenario
        updateScenario(pickMode, scenarioId, { practiceMatchups: newMatchups });
      }
    }
  };

  // Filter scenarios with matchups
  const hasValidMatchups = (s) =>
    s.practiceMatchups &&
    Array.isArray(s.practiceMatchups?.safe?.radiant) &&
    Array.isArray(s.practiceMatchups?.safe?.dire) &&
    Array.isArray(s.practiceMatchups?.mid?.radiant) &&
    Array.isArray(s.practiceMatchups?.mid?.dire) &&
    Array.isArray(s.practiceMatchups?.off?.radiant) &&
    Array.isArray(s.practiceMatchups?.off?.dire);
  const firstPickWithMatchups = scenariosFirstPick.filter(hasValidMatchups);
  const secondPickWithMatchups = scenariosSecondPick.filter(hasValidMatchups);

  const hasAnyMatchups = firstPickWithMatchups.length > 0 || secondPickWithMatchups.length > 0;

  return (
    <OverlayContainer onClick={onClose}>
      <ContentContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <Title>All Practice Matchups</Title>

        {!hasAnyMatchups ? (
          <NoMatchupsMessage>
            No practice matchups saved yet. Click "Practice Match-up" on a scenario to create one.
          </NoMatchupsMessage>
        ) : (
          <PickModeSections>
            {/* 1st Pick Matchups */}
            {firstPickWithMatchups.length > 0 && (
              <PickModeSection $isFirst={true}>
                <PickModeHeader $isFirst={true}>1st Pick Scenarios</PickModeHeader>
                <ScenariosGrid>
                  {firstPickWithMatchups.map((scenario, index) => {
                    const teamPicks = getTeamPicks(scenario);
                    return (
                      <ScenarioCard key={scenario.id}>
                        <ScenarioHeader>
                          Scenario {scenariosFirstPick.indexOf(scenario) + 1}
                          {scenario.title && ` - ${scenario.title}`}
                        </ScenarioHeader>

                        <MatchupContent>
                          {/* Left: Hero Picks */}
                          <HeroPicksColumn>
                            <TeamsRow>
                              <TeamColumn>
                                <TeamLabel $team="Radiant">Radiant</TeamLabel>
                                {[0, 1, 2, 3, 4].map(idx => (
                                  <SmallHeroBox key={`rad-${idx}`} $team="Radiant" $isEmpty={!teamPicks.radiant[idx]}>
                                    {teamPicks.radiant[idx] && (
                                      <HeroImage hero={teamPicks.radiant[idx].hero || teamPicks.radiant[idx]} width={120} height={65} />
                                    )}
                                  </SmallHeroBox>
                                ))}
                              </TeamColumn>
                              <TeamColumn>
                                <TeamLabel $team="Dire">Dire</TeamLabel>
                                {[0, 1, 2, 3, 4].map(idx => (
                                  <SmallHeroBox key={`dire-${idx}`} $team="Dire" $isEmpty={!teamPicks.dire[idx]}>
                                    {teamPicks.dire[idx] && (
                                      <HeroImage hero={teamPicks.dire[idx].hero || teamPicks.dire[idx]} width={120} height={65} />
                                    )}
                                  </SmallHeroBox>
                                ))}
                              </TeamColumn>
                            </TeamsRow>
                          </HeroPicksColumn>

                          {/* Right: Laning Matchups */}
                          <LanesContainer>
                            {/* Safe Lane */}
                            <LaneRow>
                              <LaneLabel>Safe</LaneLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.safe.radiant.map((hero, idx) => (
                                  <HeroSlot
                                    key={`safe-rad-${idx}`}
                                    $team="Radiant"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('first', scenario.id, 'safe', 'radiant', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('first', scenario.id, 'safe', 'radiant', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                              <VersusLabel>VS</VersusLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.safe.dire.map((hero, idx) => (
                                  <HeroSlot
                                    key={`safe-dire-${idx}`}
                                    $team="Dire"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('first', scenario.id, 'safe', 'dire', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('first', scenario.id, 'safe', 'dire', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                            </LaneRow>

                            {/* Mid Lane */}
                            <LaneRow>
                              <LaneLabel $lane="Mid">Mid</LaneLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.mid.radiant.map((hero, idx) => (
                                  <HeroSlot
                                    key={`mid-rad-${idx}`}
                                    $team="Radiant"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('first', scenario.id, 'mid', 'radiant', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('first', scenario.id, 'mid', 'radiant', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                              <VersusLabel>VS</VersusLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.mid.dire.map((hero, idx) => (
                                  <HeroSlot
                                    key={`mid-dire-${idx}`}
                                    $team="Dire"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('first', scenario.id, 'mid', 'dire', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('first', scenario.id, 'mid', 'dire', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                            </LaneRow>

                            {/* Off Lane */}
                            <LaneRow>
                              <LaneLabel>Off</LaneLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.off.radiant.map((hero, idx) => (
                                  <HeroSlot
                                    key={`off-rad-${idx}`}
                                    $team="Radiant"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('first', scenario.id, 'off', 'radiant', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('first', scenario.id, 'off', 'radiant', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                              <VersusLabel>VS</VersusLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.off.dire.map((hero, idx) => (
                                  <HeroSlot
                                    key={`off-dire-${idx}`}
                                    $team="Dire"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('first', scenario.id, 'off', 'dire', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('first', scenario.id, 'off', 'dire', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                            </LaneRow>
                          </LanesContainer>
                        </MatchupContent>
                      </ScenarioCard>
                    );
                  })}
                </ScenariosGrid>
              </PickModeSection>
            )}

            {/* 2nd Pick Matchups */}
            {secondPickWithMatchups.length > 0 && (
              <PickModeSection $isFirst={false}>
                <PickModeHeader $isFirst={false}>2nd Pick Scenarios</PickModeHeader>
                <ScenariosGrid>
                  {secondPickWithMatchups.map((scenario, index) => {
                    const teamPicks = getTeamPicks(scenario);
                    return (
                      <ScenarioCard key={scenario.id}>
                        <ScenarioHeader>
                          Scenario {scenariosSecondPick.indexOf(scenario) + 1}
                          {scenario.title && ` - ${scenario.title}`}
                        </ScenarioHeader>

                        <MatchupContent>
                          {/* Left: Hero Picks */}
                          <HeroPicksColumn>
                            <TeamsRow>
                              <TeamColumn>
                                <TeamLabel $team="Radiant">Radiant</TeamLabel>
                                {[0, 1, 2, 3, 4].map(idx => (
                                  <SmallHeroBox key={`rad-${idx}`} $team="Radiant" $isEmpty={!teamPicks.radiant[idx]}>
                                    {teamPicks.radiant[idx] && (
                                      <HeroImage hero={teamPicks.radiant[idx].hero || teamPicks.radiant[idx]} width={120} height={65} />
                                    )}
                                  </SmallHeroBox>
                                ))}
                              </TeamColumn>
                              <TeamColumn>
                                <TeamLabel $team="Dire">Dire</TeamLabel>
                                {[0, 1, 2, 3, 4].map(idx => (
                                  <SmallHeroBox key={`dire-${idx}`} $team="Dire" $isEmpty={!teamPicks.dire[idx]}>
                                    {teamPicks.dire[idx] && (
                                      <HeroImage hero={teamPicks.dire[idx].hero || teamPicks.dire[idx]} width={120} height={65} />
                                    )}
                                  </SmallHeroBox>
                                ))}
                              </TeamColumn>
                            </TeamsRow>
                          </HeroPicksColumn>

                          {/* Right: Laning Matchups */}
                          <LanesContainer>
                            {/* Safe Lane */}
                            <LaneRow>
                              <LaneLabel>Safe</LaneLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.safe.radiant.map((hero, idx) => (
                                  <HeroSlot
                                    key={`safe-rad-${idx}`}
                                    $team="Radiant"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('second', scenario.id, 'safe', 'radiant', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('second', scenario.id, 'safe', 'radiant', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                              <VersusLabel>VS</VersusLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.safe.dire.map((hero, idx) => (
                                  <HeroSlot
                                    key={`safe-dire-${idx}`}
                                    $team="Dire"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('second', scenario.id, 'safe', 'dire', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('second', scenario.id, 'safe', 'dire', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                            </LaneRow>

                            {/* Mid Lane */}
                            <LaneRow>
                              <LaneLabel $lane="Mid">Mid</LaneLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.mid.radiant.map((hero, idx) => (
                                  <HeroSlot
                                    key={`mid-rad-${idx}`}
                                    $team="Radiant"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('second', scenario.id, 'mid', 'radiant', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('second', scenario.id, 'mid', 'radiant', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                              <VersusLabel>VS</VersusLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.mid.dire.map((hero, idx) => (
                                  <HeroSlot
                                    key={`mid-dire-${idx}`}
                                    $team="Dire"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('second', scenario.id, 'mid', 'dire', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('second', scenario.id, 'mid', 'dire', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                            </LaneRow>

                            {/* Off Lane */}
                            <LaneRow>
                              <LaneLabel>Off</LaneLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.off.radiant.map((hero, idx) => (
                                  <HeroSlot
                                    key={`off-rad-${idx}`}
                                    $team="Radiant"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('second', scenario.id, 'off', 'radiant', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('second', scenario.id, 'off', 'radiant', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                              <VersusLabel>VS</VersusLabel>
                              <TeamSlots>
                                {scenario.practiceMatchups.off.dire.map((hero, idx) => (
                                  <HeroSlot
                                    key={`off-dire-${idx}`}
                                    $team="Dire"
                                    $isEmpty={!hero}
                                    onClick={() => handleLaneSlotClick('second', scenario.id, 'off', 'dire', idx)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (hero) handleClearSlot('second', scenario.id, 'off', 'dire', idx);
                                    }}
                                  >
                                    {hero && <HeroImage hero={hero} width={120} height={65} />}
                                  </HeroSlot>
                                ))}
                              </TeamSlots>
                            </LaneRow>
                          </LanesContainer>
                        </MatchupContent>
                      </ScenarioCard>
                    );
                  })}
                </ScenariosGrid>
              </PickModeSection>
            )}
          </PickModeSections>
        )}

        {/* Hero Selection Modal */}
        {showHeroSelect && (
          <>
            <HeroSelectOverlay onClick={() => {
              setShowHeroSelect(false);
              setHeroSearchQuery('');
            }} />
            <HeroSelectContainer onClick={(e) => e.stopPropagation()}>
              <HeroSelectHeader>
                <HeroSelectHeaderTop>
                  <HeroSelectTitle>
                    Select Hero for {selectingFor?.lane} Lane - {selectingFor?.team}
                  </HeroSelectTitle>
                  <CloseButtonSmall onClick={() => {
                    setShowHeroSelect(false);
                    setHeroSearchQuery('');
                  }}>
                    ×
                  </CloseButtonSmall>
                </HeroSelectHeaderTop>
                <SearchInput
                  type="text"
                  placeholder="Search heroes..."
                  value={heroSearchQuery}
                  onChange={(e) => setHeroSearchQuery(e.target.value)}
                  autoFocus
                />
              </HeroSelectHeader>
              <HeroGrid>
                {(heroSearchQuery
                  ? Object.values(HeroData).filter(hero => {
                      const searchTerm = heroSearchQuery.toLowerCase().trim();
                      const heroName = hero.name.toLowerCase();

                      // Direct name match
                      if (heroName.includes(searchTerm)) {
                        return true;
                      }

                      // Split hero name into words (handle spaces, hyphens, and apostrophes)
                      const words = heroName.split(/[\s\-']+/).filter(word => word.length > 0);

                      // Create abbreviation from first letters
                      const abbreviation = words.map(word => word.charAt(0)).join('');

                      // Check exact abbreviation match (e.g., "aa" -> "Ancient Apparition")
                      if (abbreviation === searchTerm) {
                        return true;
                      }

                      // Check partial abbreviation match (e.g., "a" matches "Ancient Apparition")
                      if (abbreviation.startsWith(searchTerm)) {
                        return true;
                      }

                      // Check if search term matches any word start
                      if (words.some(word => word.startsWith(searchTerm))) {
                        return true;
                      }

                      // Special case: if search is very short, be more restrictive
                      if (searchTerm.length === 1) {
                        return abbreviation.startsWith(searchTerm) || words.some(word => word.startsWith(searchTerm));
                      }

                      return false;
                    }).sort((a, b) => {
                      // Sort results by relevance
                      const searchTerm = heroSearchQuery.toLowerCase().trim();
                      const aName = a.name.toLowerCase();
                      const bName = b.name.toLowerCase();

                      // Exact name starts get priority
                      const aStartsWith = aName.startsWith(searchTerm);
                      const bStartsWith = bName.startsWith(searchTerm);
                      if (aStartsWith && !bStartsWith) return -1;
                      if (bStartsWith && !aStartsWith) return 1;

                      // Then abbreviation matches
                      const aWords = aName.split(/[\s\-']+/).filter(word => word.length > 0);
                      const bWords = bName.split(/[\s\-']+/).filter(word => word.length > 0);
                      const aAbbr = aWords.map(word => word.charAt(0)).join('');
                      const bAbbr = bWords.map(word => word.charAt(0)).join('');

                      const aAbbrMatch = aAbbr === searchTerm;
                      const bAbbrMatch = bAbbr === searchTerm;
                      if (aAbbrMatch && !bAbbrMatch) return -1;
                      if (bAbbrMatch && !aAbbrMatch) return 1;

                      // Finally alphabetical
                      return aName.localeCompare(bName);
                    })
                  : Object.values(HeroData)
                ).map(hero => (
                  <HeroSelectCard
                    key={hero.id}
                    onClick={() => handleHeroSelect(hero)}
                  >
                    <HeroImage hero={hero} size="small" />
                    <HeroName>{hero.name}</HeroName>
                  </HeroSelectCard>
                ))}
              </HeroGrid>
            </HeroSelectContainer>
          </>
        )}
      </ContentContainer>
    </OverlayContainer>
  );
};

export default AllMatchupsView;
